import React, { useState, useRef, useEffect } from 'react';
import RateOption from './RateOption';
import { useBooking } from '../context/BookingContext';

const RoomCard = ({ room, onImageClick }) => {
  const { state } = useBooking();
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const tooltipRef = useRef(null);
  const buttonRef = useRef(null);

  const getCartQuantity = (rateId) => {
    const item = state.cart.find(item => item.id === rateId && item.roomId === room.id);
    return item ? item.quantity : 0;
  };

  const totalRoomsCount = room.total_rooms || 0;

  const handleMobileToggle = () => {
    setIsMobileExpanded(!isMobileExpanded);
  };

  const getTermsAndConditions = () => {
    if (room.terms_and_conditions && 
        Array.isArray(room.terms_and_conditions) && 
        room.terms_and_conditions.length > 0) {
      return room.terms_and_conditions.filter(term => 
        term && term.trim() !== ''
      );
    }
    return [];
  };

  const termsAndConditions = getTermsAndConditions();

  // Check if room has other_offers
  const hasOtherOffers = room.other_offer && 
                        Array.isArray(room.other_offer) && 
                        room.other_offer.length > 0 && 
                        room.other_offer[0].offers && 
                        room.other_offer[0].offers.length > 0;

  // Function to handle applying the first available offer
  const handleApplyFirstOffer = () => {
    if (hasOtherOffers && room.other_offer.length > 0) {
      setSelectedOffer(room.other_offer[0]);
    }
  };

  // Function to handle clearing the selected offer
  const handleClearOffer = () => {
    setSelectedOffer(null);
  };

  // Get rates to display (either regular rates or other offer rates)
  const getRatesToDisplay = () => {
    if (selectedOffer) {
      // Return the offers from the selected special package
      return selectedOffer.offers.map(offer => ({
        id: offer.id.toString(),
        name: offer.name,
        price: offer.price,
        originalPrice: offer.originalPrice,
        offer: offer.offer || null,
        adult_pricing: offer.adult_pricing || [],
        child_pricing: offer.child_pricing || [],
        extra_bed_pricing: offer.extra_bed_pricing || [],
        inclusions: offer.inclusions || [],
        isSpecialOffer: true,
        offerName: selectedOffer.offer_name
      }));
    } else {
      // Return regular rate plans
      return room.rates || [];
    }
  };

  const ratesToDisplay = getRatesToDisplay();

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="bg-white rounded-lg overflow-hidden border border-gray-200 shadow-md">
      {/* Desktop Layout */}
      <div className="hidden md:flex flex-col md:flex-row">
        {/* Room Image */}
        <div className="md:w-4/12 relative group cursor-pointer">
          <img
            src={room.image}
            alt={room.name}
            className="w-full h-48 object-cover"
            onClick={() => onImageClick(room)}
          />
          <div 
            className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center"
            onClick={() => onImageClick(room)}
          >
            <button className="expand-icon-btn p-2 bg-white/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-90 group-hover:scale-100">
              <svg xmlns="http://www.w3.org2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 text-text-primary">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m4.5 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Room Details */}
        <div className="md:w-8/12 p-4 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-start mb-1">
              <h2 onClick={() => onImageClick(room)} className="text-xl font-bold text-text-primary tracking-tight cursor-pointer">{room.name}</h2>
              {totalRoomsCount > 0 && (
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                  {totalRoomsCount} Room{totalRoomsCount !== 1 ? 's' : ''} Available
                </span>
              )}
            </div>
            <p className="text-xs text-text-secondary mb-2 flex items-center space-x-3">
              <span>{room.size}</span>
              <span className="text-gray-300">•</span>
              <span>{room.guests}</span>
            </p>
            <p className="text-text-secondary text-sm leading-relaxed mt-2 mb-3 line-clamp-2">
              {room.description}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {room.amenities.slice(0, 4).map((amenity, index) => (
              <span key={index} className="text-xs font-medium text-text-secondary bg-slate-100 rounded px-2 py-0.5">
                {amenity}
              </span>
            ))}
            {room.amenities.length > 4 && (
              <span onClick={() => onImageClick(room)} className="text-xs font-medium text-text-secondary bg-slate-100 rounded px-2 py-0.5 cursor-pointer">
                +{room.amenities.length - 4} more
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Rate Options - Desktop */}
      <div className="hidden md:block bg-white px-4 py-4 border-t border-gray-200">
        {ratesToDisplay.length > 0 ? (
          <>
            {/* Header with rate name and View More Offers button */}
            <div className="flex justify-between items-center mb-2 relative">
              <h3 className="text-base font-semibold text-text-primary">
                {selectedOffer 
                  ? selectedOffer.offer_name 
                  : (ratesToDisplay[0]?.offer?.discount_name || 'Best Available Rate')
                }
              </h3>
              
              {/* View More Offers / Special Packages Button */}
              {hasOtherOffers && !selectedOffer && (
                <div className="relative hidden">
                  <button
                    ref={buttonRef}
                    onClick={handleApplyFirstOffer}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    className="text-sm font-medium text-brand-accent hover:text-brand-accent-hover flex items-center gap-1 relative z-10"
                  >
                    <span>View More Offers</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Tooltip */}
                  {showTooltip && (
                    <div 
                      ref={tooltipRef}
                      className="absolute right-0 top-full mt-1 w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 z-50 shadow-lg animate-in fade-in duration-200"
                    >
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Special package with extra inclusions</span>
                      </div>
                      
                      {/* Arrow pointing to button */}
                      <div className="absolute -top-1 right-3 w-3 h-3 bg-gray-900 transform rotate-45"></div>
                    </div>
                  )}
                </div>
              )}

              {/* Clear Offer Button when a special offer is selected */}
              {selectedOffer && (
                <button
                  onClick={handleClearOffer}
                  className="text-sm font-medium text-gray-600 hover:text-gray-800 flex items-center gap-1"
                >
                  <span>Back to Regular Rates</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

           

            {/* Rate Options */}
            <div className="space-y-0 divide-y divide-gray-200 pl-4">
              {ratesToDisplay.map(rate => (
                <RateOption
                  key={rate.id}
                  rate={rate}
                  room={room}
                  currentQuantity={getCartQuantity(rate.id)}
                  isSpecialOffer={rate.isSpecialOffer || false}
                />
              ))}
            </div>

            {/* Dynamic Terms and Conditions */}
            {termsAndConditions.length > 0 && (
              <div className="text-xs space-y-1 pt-1.5 border-t border-gray-200 mt-1">
                {termsAndConditions.map((term, index) => (
                  <p key={index}>
                    <span className="text-xs font-medium text-gray-700 flex items-center mt-0.5">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      {term}
                    </span>
                  </p>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-text-secondary text-sm">No rate plans available for the selected dates.</p>
        )}
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden flex flex-col bg-white">
        {/* Mobile Hero Image */}
        <div 
          className="relative h-56 w-full overflow-hidden cursor-pointer group"
          onClick={() => {
            onImageClick(room);
            setIsMobileExpanded(true);
          }}
        >
          <img
            src={room.image}
            alt={room.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          
          <div className="absolute top-3 right-3">
            <button 
              className="p-2.5 bg-white/90 rounded-full shadow-lg active:scale-95 transition-transform"
              onClick={(e) => {
                e.stopPropagation();
                onImageClick(room);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 text-gray-900">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m4.5 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              </svg>
            </button>
          </div>

          {totalRoomsCount > 0 && (
            <div className="absolute bottom-3 left-3">
              <span className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-full font-semibold shadow-lg">
                {totalRoomsCount} available
              </span>
            </div>
          )}
        </div>

        {/* Room Info Section */}
        <div className="px-4 pt-4 pb-2">
          <h2 className="text-lg font-bold text-text-primary mb-1 truncate">{room.name}</h2>
          
          <div className="flex gap-3 text-xs text-text-secondary mb-2">
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.5 1.5H3.75A2.25 2.25 0 001.5 3.75v12.5A2.25 2.25 0 003.75 18.5h12.5a2.25 2.25 0 002.25-2.25V9.5"></path>
              </svg>
              <span>{room.size}</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 10a2 2 0 100-4 2 2 0 000 4zM.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"></path>
              </svg>
              <span>{room.guests}</span>
            </div>
          </div>

          <p className="text-sm text-text-secondary line-clamp-2 mb-3">{room.description}</p>

          <div className="flex flex-wrap gap-1.5 mb-3">
            {room.amenities.slice(0, 3).map((amenity, index) => (
              <span key={index} className="text-xs font-medium text-text-secondary bg-blue-50 text-blue-700 rounded-full px-2.5 py-1">
                {amenity}
              </span>
            ))}
            {room.amenities.length > 3 && (
              <span className="text-xs font-medium text-text-secondary bg-blue-50 text-blue-700 rounded-full px-2.5 py-1">
                +{room.amenities.length - 3}
              </span>
            )}
          </div>
        </div>

        {/* Terms and Conditions Pills */}
        {termsAndConditions.length > 0 && (
          <div className="px-4 pb-3 flex flex-wrap gap-2">
            {termsAndConditions.map((term, index) => (
              <div key={index} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-full font-medium">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span className="truncate max-w-[120px]">{term}</span>
              </div>
            ))}
          </div>
        )}

        {/* Expandable Rates Section */}
        <div className="border-t border-gray-200">
          <button
            onClick={handleMobileToggle}
            className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors"
          >
            <div className="flex-1 text-left">
              <span className="font-semibold text-text-primary text-sm block">
                {selectedOffer 
                  ? selectedOffer.offer_name 
                  : (ratesToDisplay[0]?.offer?.discount_name || 'Best Available Rate')
                }
              </span>
              
              {/* Mobile View More Offers Tooltip */}
              {hasOtherOffers && !selectedOffer && (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs text-brand-accent font-medium">
                    Tap to view special packages
                  </span>
                  <div className="relative group">
                    <svg className="w-3 h-3 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-48 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg z-10">
                      <div className="flex items-start gap-2">
                        <svg className="w-3 h-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Special package with extra inclusions</span>
                      </div>
                      <div className="absolute top-full left-2 w-3 h-3 bg-gray-900 transform rotate-45"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <svg 
              className={`w-5 h-5 text-text-secondary transition-transform duration-300 ${isMobileExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </button>

          {/* Expanded Section */}
          {isMobileExpanded && (
            <div className="max-h-max overflow-hidden bg-white animate-in fade-in duration-200">
              {/* Special Packages Section for Mobile */}
              {hasOtherOffers && !selectedOffer && (
                <div className="px-4 py-3 border-b border-gray-200 bg-blue-50">
                  <div className="flex items-start gap-2 mb-2">
                    <svg className="w-4 h-4 mt-0.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="font-medium text-text-primary">Special Packages Available</h4>
                  </div>
                  <div className="space-y-2">
                    {room.other_offer.map((offerGroup, groupIndex) => (
                      <div key={groupIndex} className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <h5 className="font-medium text-text-primary">{offerGroup.offer_name}</h5>
                            <p className="text-xs text-text-secondary mt-1">Special package with extra inclusions</p>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedOffer(offerGroup);
                              setIsMobileExpanded(false);
                            }}
                            className="ml-2 px-3 py-1.5 bg-brand-accent text-white text-sm font-medium rounded hover:bg-brand-accent-hover transition-colors"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Clear Offer Button for Mobile */}
              {selectedOffer && (
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <button
                    onClick={handleClearOffer}
                    className="w-full flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800 py-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Regular Rates
                  </button>
                </div>
              )}

              {/* Rate Options */}
              <div className="px-4 py-3 space-y-0 divide-y divide-gray-150">
                {ratesToDisplay.length > 0 ? (
                  ratesToDisplay.map(rate => (
                    <RateOption
                      key={rate.id}
                      rate={rate}
                      room={room}
                      currentQuantity={getCartQuantity(rate.id)}
                      isSpecialOffer={rate.isSpecialOffer || false}
                    />
                  ))
                ) : (
                  <p className="text-text-secondary text-sm py-3">No rate plans available for the selected dates.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoomCard;